import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (matches the convention used in ar.ts /
// lva.ts / prepaid.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Enums
// ============================================================
export const PropertyTaxTypeSchema = z.enum(['LAND_BUILDING', 'SIGN']);
// At-rest status. OVERDUE is computed on read by the service and surfaced on
// the wire as a fourth value via `derivedStatus`; the DB column never stores it.
export const PropertyTaxStatusSchema = z.enum(['PENDING', 'PAID', 'CANCELLED']);
export const PropertyTaxDerivedStatusSchema = z.enum(['PENDING', 'PAID', 'CANCELLED', 'OVERDUE']);
// ============================================================
// PropertyTaxItem — list/detail row
// ============================================================
export const PropertyTaxItemSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    taxType: PropertyTaxTypeSchema,
    year: z.number().int().min(2020).max(2100),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    location: z.string(),
    appraisedValue: DecimalLike,
    taxRate: DecimalLike,
    taxAmount: DecimalLike,
    dueDate: z.coerce.date(),
    paidDate: z.coerce.date().nullable().optional(),
    status: PropertyTaxStatusSchema,
    derivedStatus: PropertyTaxDerivedStatusSchema,
    journalEntryIdAccrual: z.uuid().nullable().optional(),
    journalEntryIdPayment: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// CreatePropertyTaxItem — POST body
// Server generates voucherNo (PT-YYYY-NNNNNN) and posts the accrual JV
// (Dr 5295/5296 / Cr 2160) using taxAmount as the booked figure.
// ============================================================
export const CreatePropertyTaxItemSchema = z.object({
    taxType: PropertyTaxTypeSchema,
    year: z.number().int().min(2020).max(2100),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    location: z.string().min(1),
    appraisedValue: z.coerce.number().nonnegative(),
    taxRate: z.coerce.number().nonnegative(),
    taxAmount: z.coerce.number().positive(),
    dueDate: z.iso.date(),
    notes: z.string().optional(),
});
// ============================================================
// PayPropertyTaxItem — POST :id/pay body
// Posts payment JV (Dr accrued / Cr cashAccountCode) and flips status to PAID.
// cashAccountCode is OPTIONAL: when omitted the service resolves BANK_PRIMARY
// (the company's configured default cash account — set via /settings/account-mapping).
// ============================================================
export const PayPropertyTaxItemSchema = z.object({
    paidDate: z.iso.date(),
    cashAccountCode: z.string().trim().min(2).optional(),
});
// ============================================================
// CancelPropertyTaxItem — POST :id/cancel body
// Pure status flip — caller is responsible for any reversal JV.
// ============================================================
export const CancelPropertyTaxItemSchema = z.object({
    reason: z.string().optional(),
});
// ============================================================
// PropertyTaxSummary — GET /summary?year=YYYY
// Aggregates used by the dashboard / KPI cards. Totals are decimal-safe and
// returned as canonical strings (same convention as TrialBalance).
// ============================================================
export const PropertyTaxMonthlyBucketSchema = z.object({
    month: z.number().int().min(1).max(12),
    // Accrual amortised over 12 months — for V1 full-annual accrual this is
    // taxAmount/12 per item that's PENDING/PAID; client may compose the matrix.
    accrualAmount: z.string(),
    // Payments actually made in this month (sum of taxAmount where paidDate falls in month).
    paidAmount: z.string(),
});
export const PropertyTaxBranchBucketSchema = z.object({
    branchId: z.uuid().nullable(),
    branchName: z.string().nullable(),
    itemCount: z.number().int().nonnegative(),
    totalTax: z.string(),
    paidAmount: z.string(),
    pendingAmount: z.string(),
});
export const PropertyTaxSummarySchema = z.object({
    year: z.number().int(),
    itemCount: z.number().int().nonnegative(),
    totalTax: z.string(),
    paidAmount: z.string(),
    pendingAmount: z.string(),
    overdueCount: z.number().int().nonnegative(),
    overdueAmount: z.string(),
    byMonth: z.array(PropertyTaxMonthlyBucketSchema),
    byBranch: z.array(PropertyTaxBranchBucketSchema),
});
//# sourceMappingURL=property-tax.js.map